import { AlertIcon, ArrowLeftIcon, BookIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from "@primer/octicons-react";
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAttention, useChecks, useKnowledge, useKnowledgeDoc, useKnowledgeSearch, useLog } from "../api/hooks";
import type { CheckState, DocCheck, DocClass, DocProblem, Knowledge as KnowledgeData, KnowledgeDoc, KnowledgeType, Proof } from "../api/types";
import { rp } from "../api/client";
import { CopyButton, Empty, ErrorMessage, Loading, TimeAgo } from "../components/common";
import { Markdown } from "../components/Markdown";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Badge, toneText, type Tone } from "../components/ui/badge";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Subhead } from "../components/SettingsLayout";
import { Chip, ChipGroup } from "../components/ui/toggle-group";

const TYPE_COLORS: Record<string, string> = {
  spec: "var(--accent)",
  adr: "var(--severe)",
  rfc: "var(--merged)",
  plan: "var(--success)",
  doc: "var(--fg-muted)",
};
const EXTRA = ["var(--warning)", "var(--danger)", "var(--info)"];
const colorOf = (types: KnowledgeType[], type: string) =>
  TYPE_COLORS[type] ??
  EXTRA[
    Math.max(
      0,
      types.findIndex((t) => t.name === type),
    ) % EXTRA.length
  ];

// The three classes every status belongs to (OKF v0.2), and the words bee knows without a mapping.
const CLASSES: Array<{ id: DocClass; label: string; hint: string }> = [
  { id: "draft", label: "Draft", hint: "being written or discussed; checks not enforced" },
  { id: "stable", label: "In force", hint: "shown to agents; checks enforced" },
  { id: "deprecated", label: "Deprecated", hint: "kept for history and links" },
];
const CLASS_TONE: Record<DocClass, Tone> = { draft: "blue", stable: "green", deprecated: "default" };
const CLASS_WORDS: Record<string, DocClass> = {
  draft: "draft",
  proposed: "draft",
  review: "draft",
  wip: "draft",
  discussion: "draft",
  idea: "draft",
  open: "draft",
  superseded: "deprecated",
  rejected: "deprecated",
  deprecated: "deprecated",
  withdrawn: "deprecated",
  abandoned: "deprecated",
  done: "deprecated",
  obsolete: "deprecated",
  archived: "deprecated",
  retired: "deprecated",
};

/** The class of a document: from the server, else its type's mapping, else the known words (no status is in force). */
function classOf(types: KnowledgeType[], d: KnowledgeDoc): DocClass {
  if (d.class) return d.class;
  const s = (d.status ?? "").toLowerCase();
  return types.find((t) => t.name === d.type)?.classes?.[s] ?? CLASS_WORDS[s] ?? "stable";
}

// Team words keep their own color where it says more than the class (rejected is not just "old").
const STATUS_TONE: Record<string, Tone> = { rejected: "red", abandoned: "red", withdrawn: "red", superseded: "orange" };
const StatusBadge = ({ status, cls }: { status?: string; cls?: DocClass }) =>
  status ? <Badge tone={STATUS_TONE[status] ?? (cls ? CLASS_TONE[cls] : "default")}>{status}</Badge> : null;

const STATE: Record<CheckState, { label: string; tone: Tone; Icon: typeof CheckCircleIcon }> = {
  holds: { label: "pass", tone: "green", Icon: CheckCircleIcon },
  broken: { label: "fail", tone: "red", Icon: XCircleIcon },
  pending: { label: "not yet", tone: "yellow", Icon: ClockIcon },
};
const StateBadge = ({ state }: { state: CheckState }) => {
  const s = STATE[state] ?? STATE.pending;
  return (
    <Badge tone={s.tone}>
      <s.Icon size={12} /> {s.label}
    </Badge>
  );
};
/** A check's name: its heading title, else the id after the document slug. */
const checkName = (c: DocCheck) => c.title || c.id.slice(c.id.indexOf("/") + 1);
/** The first line of a check's section, the statement it makes; empty when the title already says it. */
const statement = (c: DocCheck) => {
  const line =
    c.text
      .split("\n")
      .find((l) => l.trim() && !/^\s*[-#]/.test(l))
      ?.trim() ?? "";
  return c.title && line.startsWith(c.title) ? "" : line;
};
const proofText = (p: Proof | string) => (typeof p === "string" ? p : `${p.kind ? `${p.kind}: ` : ""}${p.job}: ${p.file ? `${p.file}::` : ""}${p.test}`);

type View = "docs";
/** The views RepoLayout's side menu offers under Docs. */
const VIEWS: Array<{ id: View; label: string; about: string }> = [
  { id: "docs", label: "Docs", about: "" },
];

const CONFIG_EXAMPLE = `# .bee/knowledge.yaml
sources: [docs, specs, plans]
types:
  spec:
    paths: ["specs/**"]
    statuses: {draft: draft, stable: stable, deprecated: deprecated}
    checks:
      heading: "## Requirement: {id}"
      key: Verified-by
  runbook:
    label: Runbook
    paths: ["runbooks/**"]`;

/**
 * /:owner/:repo/docs — every spec, decision, proposal, plan and note, read from the repo's Markdown at main.
 * A document's status belongs to a class (draft, in force, deprecated); lines that name a test are checks,
 * and their state comes from CI on main. Types, statuses and checks are set in .bee/knowledge.yaml.
 */
export function Docs() {
  const { repo = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const q = useKnowledge(repo);
  // The title follows the side menu entry in view (RepoLayout's Docs items).
  const current = VIEWS.find((v) => v.id === params.get("view")) ?? VIEWS[0];
  const view = current.id;
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <>
      <Subhead title={current.label} />
      {q.isLoading ? (
        <Card>
          <Loading rows={8} />
        </Card>
      ) : q.error ? (
        <ErrorMessage error={q.error} />
      ) : q.data ? (
        <>
          {q.data.config_error && (
            <Alert variant="warning" className="mb-3 mt-0">
              <AlertTitle>.bee/knowledge.yaml is not valid, so the defaults apply</AlertTitle>
              <span className="font-mono text-xs">{q.data.config_error}</span>
            </Alert>
          )}
          {q.data.docs.length === 0 ? <Starter data={q.data} /> : <Body data={q.data} repo={repo} view={view} params={params} set={set} />}
        </>
      ) : null}
    </>
  );
}

/** The commands an agent runs to start this repo's docs: one `bee new` per type it knows, then land. */
function Starter({ data }: { data: KnowledgeData }) {
  const types = data.types.filter((t) => t.name !== "doc");
  const steps = [
    "# Write down what must hold and what you decided, one Markdown file per doc:",
    ...(types.length ? types : data.types).map((t) => `bee new ${t.name} "<title>"   # ${t.label}, in ${t.dir}/`),
    "# Fill in the file, then land it like code:",
    'bee land -m "<title>"',
    "# Read them back (what needs attention, a path, a check id or search words):",
    "bee docs",
  ].join("\n");
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-6">
        <Empty icon={BookIcon}>
          No documents under {data.scopes.map((s) => `${s}/`).join(", ")} on main yet. Agents create them from the CLI; hand them these steps:
        </Empty>
        <div className="relative mx-auto w-full max-w-xl">
          <pre className="overflow-x-auto rounded-md bg-neutral-muted p-3 pr-10 text-xs">{steps}</pre>
          <span className="absolute right-1 top-1">
            <CopyButton value={steps} title="Copy steps" />
          </span>
        </div>
        {!data.configured && (
          <>
            <p className="mx-auto w-full max-w-xl text-xs text-muted-foreground">
              Types, folders and checks are the defaults. To change them, land <code>.bee/knowledge.yaml</code> (a person approves it):
            </p>
            <pre className="mx-auto w-full max-w-xl overflow-x-auto rounded-md bg-neutral-muted p-3 text-xs">{CONFIG_EXAMPLE}</pre>
          </>
        )}
      </CardContent>
    </Card>
  );
}

type Setter = (p: Record<string, string | null>) => void;

function Body({ data, repo, view, params, set }: { data: KnowledgeData; repo: string; view: View; params: URLSearchParams; set: Setter }) {
  const selected = params.get("select");
  const type = params.get("type") ?? "";
  const status = params.get("status") ?? "";
  const cls = params.get("class") ?? "";
  const tag = params.get("tag") ?? "";
  const [text, setText] = useState(params.get("q") ?? "");
  const [debounced, setDebounced] = useState(text);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(text), 250);
    return () => clearTimeout(t);
  }, [text]);
  const search = useKnowledgeSearch(repo, debounced);
  const select = (id: string | null, check?: string) => set({ select: id, check: check ?? null });

  const shown = useMemo(() => {
    const searching = debounced.trim().length >= 2;
    const hits = searching ? new Map((search.data ?? []).map((h) => [h.id, h.snippet])) : null;
    const base = hits ? (search.data ?? []) : data.docs;
    // Without a class filter, deprecated documents stay out of the list, as they do for agents.
    const classOk = (d: KnowledgeDoc) =>
      cls === "all" ? true : cls ? classOf(data.types, d) === cls : classOf(data.types, d) !== "deprecated";
    return base
      .filter((d) => (!type || d.type === type) && (!status || d.status === status) && (!tag || d.tags.includes(tag)) && classOk(d))
      .map((d) => ({ doc: d as KnowledgeDoc, snippet: hits?.get(d.id) ?? "" }));
  }, [data.docs, data.types, search.data, debounced, type, status, cls, tag, view]);

  const typeInfo = data.types.find((t) => t.name === type);
  const statuses = useMemo(() => {
    const seen = new Set(
      data.docs
        .filter((d) => !type || d.type === type)
        .map((d) => d.status)
        .filter(Boolean) as string[],
    );
    const ordered = (typeInfo?.statuses ?? []).filter((s) => seen.has(s));
    return [...ordered, ...[...seen].filter((s) => !ordered.includes(s)).sort()];
  }, [data.docs, type, typeInfo]);
  const tags = useMemo(() => {
    const n = new Map<string, number>();
    for (const d of data.docs) for (const t of d.tags) n.set(t, (n.get(t) ?? 0) + 1);
    return [...n].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 24);
  }, [data.docs]);

  const sel = selected ? data.docs.find((d) => d.id === selected) : undefined;
  if (sel) {
    return <Reader repo={repo} doc={sel} data={data} select={select} focusCheck={params.get("check")} />;
  }

  const filters = (
    <div className="mb-4 flex flex-col gap-3 border-b border-border-muted pb-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-sm flex-1"
          aria-label="Search documents"
          placeholder="Search titles and text"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <ChipGroup aria-label="Type" value={type} onValueChange={(v) => set({ type: v || null, status: null })}>
          {data.types
            .filter((t) => t.count > 0 || t.name === type)
            .map((t) => (
              <Chip key={t.name} value={t.name}>
                <span className="size-2 rounded-full" style={{ background: colorOf(data.types, t.name) }} />
                {t.label} <span className="text-muted-foreground">{t.count}</span>
              </Chip>
            ))}
        </ChipGroup>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <FilterBlock title="Class">
          <ChipGroup aria-label="Class" value={cls} onValueChange={(v) => set({ class: v || null })}>
            {CLASSES.map((c) => (
              <Chip key={c.id} value={c.id} title={c.hint}>
                {c.label}
              </Chip>
            ))}
            <Chip value="all">All</Chip>
          </ChipGroup>
        </FilterBlock>
        {statuses.length > 0 && (
          <FilterBlock title="Status">
            <ChipGroup aria-label="Status" value={status} onValueChange={(v) => set({ status: v || null })}>
              {statuses.map((s) => (
                <Chip key={s} value={s}>
                  {s}
                </Chip>
              ))}
            </ChipGroup>
          </FilterBlock>
        )}
        {tags.length > 0 && (
          <FilterBlock title="Tags">
            <ChipGroup aria-label="Tag" value={tag} onValueChange={(v) => set({ tag: v || null })}>
              {tags.map(([t, n]) => (
                <Chip key={t} value={t}>
                  {t} <span className="text-muted-foreground">{n}</span>
                </Chip>
              ))}
            </ChipGroup>
          </FilterBlock>
        )}
      </div>
    </div>
  );

  return (
    <div>
      {filters}
      <div className="min-w-0">
        <NeedsAttention repo={repo} data={data} select={select} />
        <DocList data={data} items={shown} searching={debounced.trim().length >= 2} loading={search.isFetching} select={select} />
        {data.truncated && <p className="mt-2 text-xs text-muted-foreground">Showing the first documents only.</p>}
      </div>
    </div>
  );
}

function FilterBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h3 className="text-xs font-semibold uppercase text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

/**
 * What needs a person before anything else in the docs: checks a failing test breaks on main, and
 * documents that break their type's rules. Pending checks are the to-do list of their intents, and
 * drafts and plans are a class filter away. Nothing when all is well.
 */
function NeedsAttention({ repo, data, select }: { repo: string; data: KnowledgeData; select: (id: string | null, check?: string) => void }) {
  const q = useAttention(repo);
  const broken = (q.data?.checks ?? []).filter((c) => c.state === "broken");
  const problems = q.data?.problems ?? [];
  if (!broken.length && !problems.length) return null;
  const docTitle = (id: string) => data.docs.find((d) => d.id === id)?.title ?? id;
  return (
    <div className="mb-4 flex flex-col gap-4">
      {broken.length > 0 && <CheckList title="Failing checks" checks={broken} docTitle={docTitle} select={select} />}
      {problems.length > 0 && <Problems repo={repo} problems={problems} />}
    </div>
  );
}

function CheckList({
  title,
  checks,
  docTitle,
  select,
}: {
  title: string;
  checks: DocCheck[];
  docTitle: (id: string) => string;
  select: (id: string | null, check?: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        {title} <span className="font-normal text-muted-foreground">{checks.length}</span>
      </CardHeader>
      <ul className="divide-y divide-border">
        {checks.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex min-w-0 items-center gap-2">
                <StateBadge state={c.state} />
                <button type="button" onClick={() => select(c.doc, c.id)} className="truncate text-left font-semibold hover:underline">
                  {checkName(c)}
                </button>
              </span>
              {statement(c) && <span className="line-clamp-1 text-sm">{statement(c)}</span>}
              <span className="truncate text-xs text-muted-foreground">
                {docTitle(c.doc)} · <span className="font-mono">{c.id}</span>
                {c.proofs.length > 0 && <> · {c.proofs.map(proofText).join(", ")}</>}
                {c.score && <> · {c.score}</>}
              </span>
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground" title="Hand it to an agent">
              <code>bee open --task {c.id}</code>
              <CopyButton value={`bee open --task ${c.id}`} title="Copy command" />
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Problems({ repo, problems }: { repo: string; problems: DocProblem[] }) {
  return (
    <Card>
      <CardHeader>
        <span className="flex items-center gap-2">
          <AlertIcon size={16} className={toneText.orange} /> Documents that break their type's rules{" "}
          <span className="font-normal text-muted-foreground">{problems.length}</span>
        </span>
      </CardHeader>
      <ul className="divide-y divide-border">
        {problems.map((p) => (
          <li key={`${p.path}:${p.line}:${p.message}`} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-sm">
            <Link to={`${rp(repo)}/src/main/${p.path}`} className="font-mono text-xs">
              {p.path}
              {p.line > 0 ? `:${p.line}` : ""}
            </Link>
            <span className="text-muted-foreground">{p.message}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DocList({
  data,
  items,
  searching,
  loading,
  select,
}: {
  data: KnowledgeData;
  items: Array<{ doc: KnowledgeDoc; snippet: string }>;
  searching: boolean;
  loading: boolean;
  select: (id: string) => void;
}) {
  if (items.length === 0) {
    return (
      <Card>
        <Empty>{searching && loading ? "Searching…" : "No document matches."}</Empty>
      </Card>
    );
  }
  return (
    <Card>
      <ul className="divide-y divide-border">
        {items.map(({ doc: d, snippet }) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => select(d.id)}
              className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 gap-y-0.5 px-4 py-3 text-left hover:bg-accent"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="size-2.5 shrink-0 rounded-full" style={{ background: colorOf(data.types, d.type) }} />
                <span className="truncate font-semibold">{d.title}</span>
                <StatusBadge status={d.status} cls={classOf(data.types, d)} />
              </span>
              <span className="flex items-center justify-end gap-3 text-xs text-muted-foreground max-md:hidden">
                {d.tags.slice(0, 3).map((t) => (
                  <span key={t} className="rounded-full bg-neutral-muted px-2">
                    {t}
                  </span>
                ))}
                <span className="w-20 text-right">{d.date}</span>
              </span>
              <span className="col-span-2 line-clamp-1 pl-4.5 text-sm text-muted-foreground md:col-span-1">{snippet || d.summary}</span>
              <span className="truncate font-mono text-xs text-muted-foreground max-md:hidden">{d.id}</span>
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const dirOf = (p: string) => p.slice(0, Math.max(0, p.lastIndexOf("/")));

/** Resolves ../x.md against the directory of the document that links to it. */
function resolveLink(from: string, href: string): string {
  const parts = (href.startsWith("/") ? href.slice(1) : `${dirOf(from)}/${href}`).split("/").filter((s) => s && s !== ".");
  const out: string[] = [];
  for (const s of parts) s === ".." ? out.pop() : out.push(s);
  return out.join("/");
}

function Reader({
  repo,
  doc,
  data,
  select,
  focusCheck,
}: {
  repo: string;
  doc: KnowledgeDoc;
  data: KnowledgeData;
  select: (id: string | null, check?: string) => void;
  focusCheck: string | null;
}) {
  const body = useKnowledgeDoc(repo, doc.id);
  const title = (id: string) => data.docs.find((d) => d.id === id)?.title ?? id;
  const out = data.edges.filter((e) => e.src === doc.id);
  const inc = data.edges.filter((e) => e.dst === doc.id);
  const log = useLog(repo, { path: doc.id, limit: 12 });
  const cls = classOf(data.types, doc);
  const type = data.types.find((t) => t.name === doc.type);
  const replacedBy = inc.filter((e) => e.relation === "supersedes").map((e) => e.src);
  const replaces = out.filter((e) => e.relation === "supersedes").map((e) => e.dst);
  const text = useMemo(() => (body.data?.body ?? "").replace(/^\s*#\s+.*\n/, ""), [body.data]);
  // A link to another knowledge document opens it here instead of leaving for the file view.
  const onClick = (e: MouseEvent) => {
    const a = (e.target as HTMLElement).closest("a");
    const href = a?.getAttribute("href");
    if (!href || /^[a-z]+:/i.test(href) || !/\.md(#.*)?$/.test(href)) return;
    const id = resolveLink(doc.id, href.replace(/#.*$/, ""));
    if (data.docs.some((d) => d.id === id)) {
      e.preventDefault();
      select(id);
    }
  };
  return (
    <div>
      <button type="button" onClick={() => select(null)} className="mb-3 inline-flex items-center gap-1 text-sm text-info hover:underline">
        <ArrowLeftIcon size={14} /> All documents
      </button>
      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="flex min-w-0 flex-col gap-4">
          {type?.checks && <DocChecks repo={repo} doc={doc} cls={cls} focus={focusCheck} />}
          <Card className="min-w-0">
            <CardHeader>
              <span className="flex flex-wrap items-center gap-2">
                <span className="size-2.5 shrink-0 rounded-full" style={{ background: colorOf(data.types, doc.type) }} />
                <span className="min-w-0 flex-1 text-lg font-semibold">{doc.title}</span>
                <Badge>{doc.type}</Badge>
                <StatusBadge status={doc.status} cls={cls} />
              </span>
            </CardHeader>
            <CardContent>
              {replacedBy.length > 0 && (
                <Alert variant="warning" className="mb-3 mt-0">
                  Superseded by{" "}
                  {replacedBy.map((id) => (
                    <button key={id} type="button" className="text-info hover:underline" onClick={() => select(id)}>
                      {title(id)}
                    </button>
                  ))}
                  . Read that one for the current decision.
                </Alert>
              )}
              {replaces.length > 0 && (
                <p className="mb-3 text-sm text-muted-foreground">
                  Replaces{" "}
                  {replaces.map((id) => (
                    <button key={id} type="button" className="text-info hover:underline" onClick={() => select(id)}>
                      {title(id)}
                    </button>
                  ))}
                  .
                </p>
              )}
              {body.isLoading ? (
                <Loading rows={6} />
              ) : body.error ? (
                <ErrorMessage error={body.error} />
              ) : (
                // biome-ignore lint/a11y/useKeyWithClickEvents: links inside keep their own keyboard handling
                <div onClick={onClick}>
                  <Markdown source={text} />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div className="flex min-w-0 flex-col gap-4 self-start">
          <div className="flex flex-col pt-1 text-sm [&>*+*]:mt-3 [&>*+*]:border-t [&>*+*]:border-border-muted [&>*+*]:pt-3">
            <div className="flex flex-col gap-3">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Class</dt>
                <dd>{CLASSES.find((c) => c.id === cls)?.label}</dd>
                {doc.date && (
                  <>
                    <dt className="text-muted-foreground">Date</dt>
                    <dd>{doc.date}</dd>
                  </>
                )}
                {doc.owner && (
                  <>
                    <dt className="text-muted-foreground">Owner</dt>
                    <dd>{doc.owner}</dd>
                  </>
                )}
                <dt className="text-muted-foreground">File</dt>
                <dd className="truncate font-mono text-xs">
                  <Link to={`${rp(repo)}/src/main/${doc.id}`} title={doc.id}>
                    {doc.id}
                  </Link>
                </dd>
              </dl>
              {doc.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {doc.tags.map((t) => (
                    <span key={t} className="rounded-full bg-neutral-muted px-2 text-xs text-muted-foreground">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {doc.paths.length > 0 && (
                <Section title="Code">
                  <ul className="flex flex-col gap-1">
                    {doc.paths.map((p) => (
                      <li key={p} className="flex items-center gap-2 font-mono text-xs">
                        <Link to={`${rp(repo)}/src/main/${p}`} className="min-w-0 flex-1 truncate" title={p}>
                          {p}
                        </Link>
                        <Link
                          to={`${rp(repo)}/graph?${p.includes(".") ? "focus" : "paths"}=${encodeURIComponent(p)}`}
                          className="font-sans text-xs text-muted-foreground"
                        >
                          graph
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
              {(log.data?.entries.length ?? 0) > 0 && (
                <Section title="History">
                  <ul className="flex flex-col gap-1.5">
                    {log.data?.entries.map((e) => (
                      <li key={e.seq} className="text-xs">
                        <span className="block truncate">
                          {e.change_id ? <Link to={`${rp(repo)}/changes/${e.change_id}`}>{e.subject ?? e.change_id}</Link> : (e.subject ?? "change")}
                        </span>
                        <span className="text-muted-foreground">
                          {e.agent ?? e.actor} · <TimeAgo ms={e.ts} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
              <Links title="Links to" edges={out} other={(e) => e.dst} name={title} select={select} />
              <Links title="Linked from" edges={inc} other={(e) => e.src} name={title} select={select} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The document's checks and what CI on main says about each; only documents in force have checks that count. */
function DocChecks({ repo, doc, cls, focus }: { repo: string; doc: KnowledgeDoc; cls: DocClass; focus: string | null }) {
  const q = useChecks(repo);
  const mine = useMemo(() => (q.data ?? []).filter((c) => c.doc === doc.id), [q.data, doc.id]);
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focus && mine.some((c) => c.id === focus)) ref.current?.scrollIntoView({ block: "center" });
  }, [focus, mine]);
  if (cls !== "stable") {
    return (
      <Alert className="mt-0">
        {cls === "draft" ? "A draft: its checks are not enforced yet. Land a status in force to enforce them." : "Deprecated: its checks no longer count."}
      </Alert>
    );
  }
  if (q.isLoading || q.data === null) return null;
  if (q.error) return <ErrorMessage error={q.error} />;
  return (
    <Card>
      <CardHeader>
        Checks <span className="font-normal text-muted-foreground">{mine.length}</span>
      </CardHeader>
      {mine.length === 0 ? (
        <Empty>No checks: give a requirement a proof line naming its test.</Empty>
      ) : (
        <ul className="divide-y divide-border">
          {mine.map((c) => (
            <li key={c.id} ref={c.id === focus ? ref : undefined} className={`flex flex-col gap-1 px-4 py-3 ${c.id === focus ? "bg-accent" : ""}`}>
              <span className="flex flex-wrap items-center gap-2">
                <StateBadge state={c.state} />
                <span className="min-w-0 flex-1 font-semibold">{checkName(c)}</span>
                <Link to={`${rp(repo)}/src/main/${c.doc}`} className="font-mono text-xs text-muted-foreground" title={`line ${c.line}`}>
                  {c.id}
                </Link>
              </span>
              {statement(c) && <p className="text-sm">{statement(c)}</p>}
              <ul className="flex flex-col gap-0.5 pl-1 text-xs">
                {c.proofs.map((p) => {
                  const ev = c.evidence.find((e) => proofText(e.proof) === proofText(p));
                  return (
                    <li key={proofText(p)} className="flex flex-wrap items-center gap-2">
                      {ev ? <StateBadge state={ev.state} /> : <StateBadge state="pending" />}
                      <code>{proofText(p)}</code>
                      {ev?.commit && <span className="font-mono text-muted-foreground">{ev.commit.slice(0, 10)}</span>}
                    </li>
                  );
                })}
                {c.score && (
                  <li className="text-muted-foreground">
                    Score: <code>{c.score}</code>
                    {c.metric !== undefined ? ` · measured ${c.metric}` : " · not measured yet"}
                  </li>
                )}
              </ul>
              {c.reason && <p className="text-xs text-muted-foreground">{c.reason}</p>}
              {c.state !== "holds" && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <code>bee open --task {c.id}</code>
                  <CopyButton value={`bee open --task ${c.id}`} title="Copy command" />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{title}</h3>
      {children}
    </div>
  );
}

function Links({
  title,
  edges,
  other,
  name,
  select,
}: {
  title: string;
  edges: KnowledgeData["edges"];
  other: (e: KnowledgeData["edges"][number]) => string;
  name: (id: string) => string;
  select: (id: string) => void;
}) {
  if (edges.length === 0) return null;
  return (
    <Section title={title}>
      <ul className="flex flex-col gap-1">
        {edges.map((e) => (
          <li key={`${e.src}>${e.relation}>${e.dst}`} className="flex items-center gap-2">
            <span className="shrink-0 rounded-full bg-neutral-muted px-2 text-xs text-muted-foreground">{e.relation}</span>
            <button type="button" className="min-w-0 flex-1 truncate text-left text-info hover:underline" onClick={() => select(other(e))}>
              {name(other(e))}
            </button>
          </li>
        ))}
      </ul>
    </Section>
  );
}
